import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as LucideIcons from "lucide-react";
import {
  GraduationCap, Search, Plus, Pencil, Trash2, ChevronRight, ChevronDown,
  BookOpen, Loader2, Video, ExternalLink, Save, FileText, X,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import VideoScriptsManager from "./VideoScriptsManager";
import AuditoriaPlataformaPage from "./auditoria/AuditoriaPlataformaPage";

const Ic = ({ name, className }) => {
  const C = LucideIcons[name] || BookOpen;
  return <C className={className} />;
};

const MD = {
  h1: (p) => <h1 className="mb-2 mt-6 font-heading text-2xl font-bold text-foreground" {...p} />,
  h2: (p) => <h2 className="mb-2 mt-7 font-heading text-lg font-bold text-foreground" {...p} />,
  h3: (p) => <h3 className="mb-1.5 mt-5 font-heading text-base font-semibold text-foreground" {...p} />,
  p: (p) => <p className="my-2.5 text-sm leading-relaxed text-foreground/90" {...p} />,
  ul: (p) => <ul className="my-2.5 list-disc space-y-1 pl-5 text-sm leading-relaxed text-foreground/90" {...p} />,
  ol: (p) => <ol className="my-2.5 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-foreground/90" {...p} />,
  li: (p) => <li className="pl-0.5" {...p} />,
  strong: (p) => <strong className="font-semibold text-foreground" {...p} />,
  a: (p) => <a className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
  blockquote: (p) => <blockquote className="my-3 rounded-r-md border-l-2 border-primary/50 bg-muted/40 px-3 py-2 text-sm text-muted-foreground" {...p} />,
  code: (p) => <code className="rounded bg-muted px-1 py-0.5 text-[0.85em]" {...p} />,
  hr: () => <hr className="my-5 border-border" />,
  table: (p) => <table className="my-3 w-full border-collapse text-sm" {...p} />,
  th: (p) => <th className="border border-border bg-muted px-2 py-1 text-left text-xs font-semibold" {...p} />,
  td: (p) => <td className="border border-border px-2 py-1" {...p} />,
};
const Markdown = ({ children }) => <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{children}</ReactMarkdown>;

function loomEmbed(url) {
  if (!url) return null;
  const m = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
  return m ? `https://www.loom.com/embed/${m[1]}` : null;
}
const isVideoFile = (url) => !!url && /\.(mp4|webm|mov|m4v)$/i.test(url);
const vttFor = (url) => (url ? url.replace(/\.[^./]+$/, ".vtt") : "");

// ================= Editor de artículo =================
function ArticleEditor({ open, onClose, article, categories, defaultCategoryId, onSaved }) {
  const editing = !!article?.id;
  const [form, setForm] = useState(() => ({
    category_id: article?.category_id || defaultCategoryId || categories[0]?.id || "",
    title: article?.title || "",
    summary: article?.summary || "",
    content: article?.content || "## ¿Qué es?\n\n## ¿Para qué sirve?\n\n## Cómo se usa\n1. \n\n## Consejos\n- ",
    video_url: article?.video_url || "",
    module_key: article?.module_key || "",
    published: article?.published ?? true,
  }));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const mut = useMutation({
    mutationFn: async () => {
      const payload = { ...form, tags: [], video_url: form.video_url || null, module_key: form.module_key || null };
      return editing
        ? (await api.put(`/ayuda/articles/${article.id}`, payload)).data
        : (await api.post(`/ayuda/articles`, payload)).data;
    },
    onSuccess: (d) => { toast.success(editing ? "Artículo actualizado" : "Artículo creado"); onSaved && onSaved(d); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ayuda-article-editor">
        <DialogHeader><DialogTitle>{editing ? "Editar artículo" : "Nuevo artículo"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Sección</Label>
              <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                <SelectTrigger data-testid="ayuda-art-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Vídeo Loom (URL, opcional)</Label>
              <Input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://www.loom.com/share/…" data-testid="ayuda-art-video" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Título</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="ayuda-art-title" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Resumen (1 línea)</Label>
            <Input value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="ayuda-art-summary" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Contenido (Markdown)</Label>
            <Tabs defaultValue="edit">
              <TabsList><TabsTrigger value="edit">Editar</TabsTrigger><TabsTrigger value="preview">Vista previa</TabsTrigger></TabsList>
              <TabsContent value="edit">
                <Textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={14} className="font-mono text-xs" data-testid="ayuda-art-content" />
              </TabsContent>
              <TabsContent value="preview">
                <div className="rounded-md border p-4"><Markdown>{form.content || "_(vacío)_"}</Markdown></div>
              </TabsContent>
            </Tabs>
          </div>
          <div className="flex items-center gap-2 rounded-md border px-3 py-2">
            <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="ayuda-art-published" />
            <Label className="text-xs">Publicado (visible para todo el equipo)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !form.title || !form.category_id} data-testid="ayuda-art-save">
            {mut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================= Editor de sección =================
function CategoryEditor({ open, onClose, category, onSaved }) {
  const editing = !!category?.id;
  const [form, setForm] = useState({ title: category?.title || "", icon: category?.icon || "BookOpen", description: category?.description || "" });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const mut = useMutation({
    mutationFn: async () => editing
      ? (await api.put(`/ayuda/categories/${category.id}`, form)).data
      : (await api.post(`/ayuda/categories`, form)).data,
    onSuccess: () => { toast.success(editing ? "Sección actualizada" : "Sección creada"); onSaved && onSaved(); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ayuda-category-editor">
        <DialogHeader><DialogTitle>{editing ? "Editar sección" : "Nueva sección"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Título</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="ayuda-cat-title" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Icono (nombre Lucide, p. ej. Plane, BookOpen)</Label>
            <div className="flex items-center gap-2">
              <Input value={form.icon} onChange={(e) => set("icon", e.target.value)} data-testid="ayuda-cat-icon" />
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border"><Ic name={form.icon} className="h-4 w-4" /></span>
            </div></div>
          <div className="space-y-1.5"><Label className="text-xs">Descripción</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="ayuda-cat-desc" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !form.title} data-testid="ayuda-cat-save">
            {mut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AyudaPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [openCats, setOpenCats] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [artEditor, setArtEditor] = useState(null);   // {article?, defaultCategoryId?}
  const [catEditor, setCatEditor] = useState(null);   // {category?}
  const [delTarget, setDelTarget] = useState(null);   // {type, id, name}
  const [tab, setTab] = useState("manual");

  useEffect(() => {
    const a = searchParams.get("article");
    if (a) {
      setSelectedId(a);
      searchParams.delete("article");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line

  const ov = useQuery({ queryKey: ["ayuda-overview"], queryFn: async () => (await api.get("/ayuda/overview")).data });
  const categories = ov.data?.categories || [];
  const canEdit = !!ov.data?.can_edit;

  const arts = useQuery({
    queryKey: ["ayuda-articles", search],
    queryFn: async () => (await api.get("/ayuda/articles", { params: { q: search.trim() || undefined } })).data,
  });
  const allArticles = arts.data?.items || [];
  const byCat = useMemo(() => {
    const m = {};
    for (const a of allArticles) (m[a.category_id] ||= []).push(a);
    return m;
  }, [allArticles]);

  const detail = useQuery({
    queryKey: ["ayuda-article", selectedId],
    queryFn: async () => (await api.get(`/ayuda/articles/${selectedId}`)).data,
    enabled: !!selectedId,
  });
  const current = detail.data;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["ayuda-overview"] });
    qc.invalidateQueries({ queryKey: ["ayuda-articles"] });
  };

  const delMut = useMutation({
    mutationFn: async () => (await api.delete(`/ayuda/${delTarget.type}/${delTarget.id}`)).data,
    onSuccess: () => {
      toast.success("Eliminado");
      if (delTarget.type === "articles" && selectedId === delTarget.id) setSelectedId(null);
      setDelTarget(null); invalidate();
    },
    onError: (e) => { toast.error(apiErrorMessage(e)); setDelTarget(null); },
  });

  const toggleCat = (id) => setOpenCats((s) => ({ ...s, [id]: !s[id] }));
  const openArticle = (a) => setSelectedId(a.id);
  const searching = search.trim().length > 0;

  if (searchParams.get("view") === "auditoria") return <AuditoriaPlataformaPage />;

  return (
    <div className="space-y-5" data-testid="ayuda-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><GraduationCap className="h-6 w-6 text-primary" />Ayuda y formación</h1>
          <p className="text-sm text-muted-foreground">Manual de uso de SOFI: cómo funciona cada módulo y para qué sirve. Se irá ampliando con vídeos y formación.</p>
        </div>
        {canEdit && tab === "manual" && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setCatEditor({})} data-testid="ayuda-new-category"><Plus className="mr-1.5 h-4 w-4" />Nueva sección</Button>
            <Button size="sm" onClick={() => setArtEditor({ defaultCategoryId: categories[0]?.id })} data-testid="ayuda-new-article"><Plus className="mr-1.5 h-4 w-4" />Nuevo artículo</Button>
          </div>
        )}
      </div>

      {canEdit && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="ayuda-tabs">
            <TabsTrigger value="manual" data-testid="ayuda-tab-manual">Manual</TabsTrigger>
            <TabsTrigger value="videos" data-testid="ayuda-tab-videos">Guiones de vídeo</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {(!canEdit || tab === "manual") ? (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
        {/* Sidebar */}
        <Card className="h-fit lg:sticky lg:top-4">
          <CardContent className="p-3">
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar en la ayuda…" className="h-9 pl-8" data-testid="ayuda-search" />
            </div>

            {ov.isLoading ? <Skeleton className="h-64 w-full" /> : searching ? (
              <div className="space-y-1" data-testid="ayuda-search-results">
                {allArticles.length === 0 && <p className="px-2 py-6 text-center text-xs text-muted-foreground">Sin resultados.</p>}
                {allArticles.map((a) => (
                  <button key={a.id} onClick={() => openArticle(a)} data-testid={`ayuda-result-${a.id}`}
                    className={`block w-full rounded-md px-2.5 py-2 text-left text-sm transition-colors ${selectedId === a.id ? "bg-accent text-accent-foreground" : "hover:bg-muted"}`}>
                    <span className="font-medium">{a.title}</span>
                    {a.summary && <span className="block truncate text-[11px] text-muted-foreground">{a.summary}</span>}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-0.5" data-testid="ayuda-nav">
                {categories.map((c) => {
                  const open = !!openCats[c.id];
                  const items = byCat[c.id] || [];
                  return (
                    <div key={c.id}>
                      <div className="group flex items-center">
                        <button onClick={() => toggleCat(c.id)} data-testid={`ayuda-cat-${c.id}`}
                          className="flex flex-1 items-center gap-2 rounded-md px-2 py-2 text-left text-sm font-medium hover:bg-muted">
                          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          <Ic name={c.icon} className="h-4 w-4 shrink-0 text-primary" />
                          <span className="flex-1 truncate">{c.title}</span>
                          <Badge variant="secondary" className="text-[10px]">{c.article_count}</Badge>
                        </button>
                        {canEdit && (
                          <button onClick={() => setCatEditor({ category: c })} title="Editar sección" data-testid={`ayuda-cat-edit-${c.id}`}
                            className="ml-0.5 hidden rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground group-hover:block"><Pencil className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                      {open && (
                        <div className="ml-4 border-l pl-2">
                          {items.length === 0 && <p className="px-2 py-1.5 text-[11px] text-muted-foreground">Sin artículos.</p>}
                          {items.map((a) => (
                            <button key={a.id} onClick={() => openArticle(a)} data-testid={`ayuda-article-link-${a.id}`}
                              className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors ${selectedId === a.id ? "bg-accent font-medium text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                              {!a.published && <Badge variant="outline" className="px-1 py-0 text-[9px]">borrador</Badge>}
                              <span className="truncate">{a.title}</span>
                            </button>
                          ))}
                          {canEdit && (
                            <button onClick={() => setArtEditor({ defaultCategoryId: c.id })} data-testid={`ayuda-add-article-${c.id}`}
                              className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[12px] text-primary hover:bg-muted"><Plus className="h-3.5 w-3.5" />Añadir artículo</button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content */}
        <div>
          {!selectedId ? (
            <Card><CardContent className="flex flex-col items-center justify-center gap-3 p-16 text-center" data-testid="ayuda-empty">
              <BookOpen className="h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Selecciona un módulo en el índice para ver cómo funciona.</p>
            </CardContent></Card>
          ) : detail.isLoading ? <Skeleton className="h-96 w-full" /> : current ? (
            <Card>
              <CardContent className="p-6 sm:p-8" data-testid="ayuda-article">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-heading text-2xl font-bold tracking-tight" data-testid="ayuda-article-title">{current.title}</h2>
                    {current.summary && <p className="mt-1 text-sm text-muted-foreground">{current.summary}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex shrink-0 gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => setArtEditor({ article: current })} data-testid="ayuda-edit-article"><Pencil className="mr-1.5 h-3.5 w-3.5" />Editar</Button>
                      <Button variant="outline" size="icon" className="h-9 w-9 text-destructive hover:text-destructive" onClick={() => setDelTarget({ type: "articles", id: current.id, name: current.title })} data-testid="ayuda-delete-article"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>

                {loomEmbed(current.video_url) && (
                  <div className="mb-5 overflow-hidden rounded-xl border" style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
                    <iframe src={loomEmbed(current.video_url)} title="Vídeo" allowFullScreen style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
                  </div>
                )}
                {isVideoFile(current.video_url) && !loomEmbed(current.video_url) && (
                  <video controls preload="metadata" className="mb-5 w-full overflow-hidden rounded-xl border bg-black" data-testid="ayuda-video">
                    <source src={current.video_url} />
                    <track default kind="subtitles" srcLang="es" label="Español" src={vttFor(current.video_url)} />
                  </video>
                )}
                {current.video_url && !loomEmbed(current.video_url) && !isVideoFile(current.video_url) && (
                  <a href={current.video_url} target="_blank" rel="noreferrer" className="mb-5 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><Video className="h-4 w-4" />Ver vídeo <ExternalLink className="h-3.5 w-3.5" /></a>
                )}

                <article className="max-w-none" data-testid="ayuda-article-body"><Markdown>{current.content || ""}</Markdown></article>

                {current.module_key && (
                  <div className="mt-6 border-t pt-4">
                    <a href={`/app/${current.module_key}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="ayuda-goto-module">
                      <ExternalLink className="h-4 w-4" />Ir al módulo
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
        </div>
      ) : (
        <VideoScriptsManager />
      )}

      {artEditor && (
        <ArticleEditor open onClose={() => setArtEditor(null)} article={artEditor.article} defaultCategoryId={artEditor.defaultCategoryId}
          categories={categories} onSaved={(d) => { invalidate(); if (d?.id) setSelectedId(d.id); }} />
      )}
      {catEditor && (
        <CategoryEditor open onClose={() => setCatEditor(null)} category={catEditor.category} onSaved={invalidate} />
      )}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar “{delTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => delMut.mutate()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="ayuda-confirm-delete">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
